import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Chip, InputAdornment, Link, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { Candidato, Candidatos, exportarCandidatos, getCandidatosCautelar } from "../services/oece.service";
import { C, Pill, fecha, gridSx, num } from "./oeceUi";
import { BotonExportar, celdaContacto } from "./OeceTabs";

const localeText = esES.components.MuiDataGrid.defaultProps.localeText;

type Prioridad = Candidato["prioridad"];

const PRIORIDADES: Record<Prioridad, { label: string; detalle: string; color: string; bg: string }> = {
  alta: { label: "Prioridad alta", detalle: "70 puntos o más. Llamar primero.", color: C.red, bg: C.redSoft },
  media: { label: "Prioridad media", detalle: "Entre 45 y 69 puntos.", color: "#8a6a1c", bg: C.goldSoft },
  baja: { label: "Prioridad baja", detalle: "Menos de 45 puntos.", color: C.muted, bg: "#eef1f5" },
};

const FILTROS = [
  { key: "en_plazo", label: "En plazo para demandar" },
  { key: "obras", label: "Rubro obras" },
  { key: "contacto", label: "Con contacto" },
  { key: "precedentes", label: "Con precedentes" },
] as const;
type Filtro = (typeof FILTROS)[number]["key"];

/* Círculo con el puntaje; al pasar el mouse explica de dónde sale */
const Puntaje = ({ c }: { c: Candidato }) => {
  const p = PRIORIDADES[c.prioridad];
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          {c.motivos_puntaje.map((m) => (
            <Stack key={m.motivo} direction="row" spacing={1} sx={{ fontSize: 12.5, lineHeight: 1.6 }}>
              <b style={{ minWidth: 26 }}>+{m.puntos}</b>
              <span>{m.motivo}</span>
            </Stack>
          ))}
        </Box>
      }
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: "50%", p: "4px", flexShrink: 0,
            background: `conic-gradient(${p.color} ${c.puntaje * 3.6}deg, ${p.bg} 0deg)`,
          }}
        >
          <Box
            sx={{
              width: "100%", height: "100%", borderRadius: "50%", bgcolor: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 14, color: p.color,
            }}
          >
            {c.puntaje}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: p.color, textTransform: "capitalize" }}>{c.prioridad}</Typography>
      </Stack>
    </Tooltip>
  );
};

interface Props {
  onVerFicha: (ruc: string) => void;
}

const CandidatosTab = ({ onVerFicha }: Props) => {
  const [data, setData] = useState<Candidatos | null>(null);
  const [error, setError] = useState("");
  const [prioridad, setPrioridad] = useState<Prioridad | "">("");
  const [filtros, setFiltros] = useState<Set<Filtro>>(new Set());
  const [texto, setTexto] = useState("");

  useEffect(() => {
    getCandidatosCautelar()
      .then((r) => setData(r.data))
      .catch(() => setError("No se pudieron cargar los candidatos."));
  }, []);

  const alternar = (f: Filtro) =>
    setFiltros((prev) => {
      const n = new Set(prev);
      if (n.has(f)) n.delete(f);
      else n.add(f);
      return n;
    });

  const filas = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (c) =>
        (!prioridad || c.prioridad === prioridad) &&
        (!filtros.has("en_plazo") || c.en_plazo_contencioso) &&
        (!filtros.has("obras") || c.obras) &&
        (!filtros.has("contacto") || c.emails.length + c.telefonos.length > 0) &&
        (!filtros.has("precedentes") || c.precedentes > 0) &&
        (!q || [c.razon_social, c.ruc, c.expediente, c.resolucion].some((v) => v?.toLowerCase().includes(q)))
    );
  }, [data, prioridad, filtros, texto]);

  const columnas: GridColDef<Candidato>[] = [
    {
      field: "puntaje", headerName: "Prioridad", width: 125,
      renderCell: ({ row }) => <Puntaje c={row} />,
    },
    {
      field: "razon_social", headerName: "Empresa", flex: 1.5, minWidth: 220,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0, py: 1 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: C.navy }} title={row.razon_social ?? ""}>
            {row.razon_social || "Sin razón social"}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>{row.ruc}</Typography>
            {row.obras && <Pill tono="blue">Obras</Pill>}
          </Stack>
        </Box>
      ),
    },
    {
      field: "meses_restantes", headerName: "Sanción", width: 165,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.red }}>
            {row.definitiva ? "DEFINITIVA" : row.tipo_sancion}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            {row.definitiva ? "Sin fecha de fin" : row.meses_restantes !== null ? `Le quedan ${Math.round(row.meses_restantes)} meses` : row.periodo}
          </Typography>
        </Box>
      ),
    },
    {
      field: "dias_para_limite", headerName: "Plazo demanda", width: 150,
      renderCell: ({ row }) =>
        row.en_plazo_contencioso ? (
          <Stack spacing={0.25} alignItems="flex-start">
            <Pill tono={row.dias_para_limite! <= 15 ? "red" : "green"}>Quedan {row.dias_para_limite} días</Pill>
            <Typography sx={{ fontSize: 11.5, color: C.muted, pl: 0.5, lineHeight: 1.3 }}>hasta {fecha(row.limite_contencioso)}</Typography>
          </Stack>
        ) : (
          <Tooltip title="Pasaron más de 3 meses desde la última resolución. Evaluar otra vía (por ejemplo, amparo).">
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>Plazo vencido</Typography>
          </Tooltip>
        ),
    },
    {
      field: "resolucion", headerName: "Resolución", width: 175,
      renderCell: ({ row }) => (
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.resolucion}</Typography>
            {row.url_resolucion && (
              <Tooltip title="Ver resolución (PDF)">
                <Link href={row.url_resolucion} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} sx={{ display: "flex", color: C.blue }}>
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />
                </Link>
              </Tooltip>
            )}
          </Stack>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            {row.expediente ? `Exp. ${row.expediente}` : fecha(row.fecha_resolucion)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "precedentes", headerName: "Precedentes", flex: 1.3, minWidth: 220,
      renderCell: ({ row }) =>
        row.precedentes ? (
          <Tooltip title={`Donde más se concedieron: ${row.organos_precedentes.map((o) => `${o.organo} (${o.n})`).join(" · ")}`}>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: C.navy }}>
                {row.precedentes} cautelares concedidas
              </Typography>
              <Typography noWrap sx={{ fontSize: 12, color: C.muted }}>
                por {row.infracciones.join(" / ").toLowerCase()}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: C.muted }}>
            {row.infracciones.length ? `Sin precedentes por ${row.infracciones.join(" / ").toLowerCase()}` : "Infracción no identificada"}
          </Typography>
        ),
    },
    {
      field: "contacto", headerName: "Contacto", width: 150, sortable: false,
      renderCell: ({ row }) => celdaContacto(row.emails, row.telefonos),
    },
  ];

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, color: "#334155", mb: 2, maxWidth: 900 }}>
        Empresas <b>sancionadas que todavía no tienen medida cautelar</b>, ordenadas por cuánto les conviene
        conseguirla. Pasa el mouse sobre el puntaje para ver de dónde sale.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2.5 }}>
        {(Object.keys(PRIORIDADES) as Prioridad[]).map((k) => {
          const p = PRIORIDADES[k];
          const activo = prioridad === k;
          return (
            <Box
              key={k}
              onClick={() => setPrioridad(activo ? "" : k)}
              sx={{
                cursor: "pointer", borderRadius: 3, p: 2, display: "flex", alignItems: "center", gap: 2,
                bgcolor: activo ? p.bg : "#fff", border: `1.5px solid ${activo ? p.color : C.border}`,
                transition: "all .15s", "&:hover": { borderColor: p.color },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, color: C.navy }}>{p.label}</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.muted }}>{p.detalle}</Typography>
              </Box>
              <Typography sx={{ fontSize: 32, fontWeight: 800, color: p.color }}>
                {data ? num(data.por_prioridad[k]) : "…"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar empresa, RUC o expediente…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          sx={{ minWidth: { md: 300 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment>,
            sx: { bgcolor: "#fff", borderRadius: 2 },
          }}
        />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {FILTROS.map((f) => {
            const activo = filtros.has(f.key);
            return (
              <Chip
                key={f.key}
                label={f.label}
                onClick={() => alternar(f.key)}
                sx={{
                  fontWeight: 600, border: `1px solid ${activo ? C.navy2 : C.border}`,
                  bgcolor: activo ? C.navy2 : "#fff", color: activo ? "#fff" : C.navy,
                  "&:hover": { bgcolor: activo ? C.navy : "#eef2f8" },
                }}
              />
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: { md: "auto !important" } }}>
          <Typography sx={{ fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>
            <b style={{ color: C.navy }}>{num(filas.length)}</b> de {num(data?.total)}
          </Typography>
          <BotonExportar onClick={exportarCandidatos} />
        </Stack>
      </Stack>

      <DataGrid
        rows={filas}
        columns={columnas}
        getRowId={(r) => r.ruc}
        loading={!data}
        rowHeight={66}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={({ row }) => onVerFicha(row.ruc)}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        localeText={localeText}
        sx={gridSx}
      />

      <Typography sx={{ fontSize: 12, color: C.muted, mt: 1.5 }}>
        Es una priorización comercial, no un análisis legal: que la empresa quiera el servicio no significa que el juez
        vaya a conceder la cautelar. Los precedentes cuentan solo cautelares vigentes publicadas por OECE (no las denegadas).
      </Typography>
    </Box>
  );
};

export default CandidatosTab;
